import axiosInstance from "./axiosInstance";
import axios from "axios";

// Track pending requests
const pendingRequests = {};

/**
 * Get answers in batches (paginated)
 * @param {number} page - The page number
 * @param {number} limit - Number of items per page
 * @param {string} search - Optional search query
 * @param {string} status - Optional status filter
 * @param {string} email - Optional email filter
 * @param {string|number} orgid - Optional organization ID filter
 * @returns {Promise} - Promise with answer data and pagination
 */
export const getAnswers = async (
  page = 1,
  limit = 20,
  search = null,
  status = null,
  email = null,
  orgid = null
) => {
  // Cancel previous requests for the same endpoint
  const requestKey = `answers_${page}_${limit}_${search}_${status}_${email}_${orgid}`;
  if (pendingRequests[requestKey]) {
    pendingRequests[requestKey].cancel("Operation canceled due to new request");
  }

  // Create a new cancel token
  const cancelToken = axios.CancelToken.source();
  pendingRequests[requestKey] = cancelToken;

  try {
    const params = { page, limit };

    // Add search and status params if they exist
    if (search) params.search = search;
    if (status) params.status = status;
    if (email) params.email = email;
    if (orgid) params.orgid = orgid;

    const response = await axiosInstance.get("/chat/answers", {
      params,
      cancelToken: cancelToken.token,
    });
    delete pendingRequests[requestKey];

    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error("Error fetching answers:", error);
      delete pendingRequests[requestKey];
      throw error;
    }
    throw error;
  }
};

/**
 * Get logs for a specific answer by its ID
 * @param {string|number} answerId - The ID of the answer
 * @returns {Promise} - Promise with the answer logs data
 */
export const getAnswerLogs = async (answerId) => {
  // Cancel previous requests for the same answerId
  const requestKey = `answer_logs_${answerId}`;
  if (pendingRequests[requestKey]) {
    pendingRequests[requestKey].cancel("Operation canceled due to new request");
  }

  // Create a new cancel token
  const cancelToken = axios.CancelToken.source();
  pendingRequests[requestKey] = cancelToken;

  try {
    const response = await axiosInstance.get(`/chat/answers/${answerId}/logs`, {
      cancelToken: cancelToken.token,
    });
    delete pendingRequests[requestKey];

    return response.data;
  } catch (error) {
    if (!axios.isCancel(error)) {
      console.error(`Error fetching logs for answer ${answerId}:`, error);
      delete pendingRequests[requestKey];
      throw error;
    }
    throw error;
  }
};

export const getBlockTests = async (
  page = 1,
  limit = 20,
  name = null,
  orgId = null
) => {
  const params = {
    page,
    limit,
  };

  if (name) {
    params.name = name;
  }

  if (orgId) {
    params.orgid = orgId;
  }

  try {
    const response = await axiosInstance.get(`/test-blocks`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching block tests:", error);
    throw error;
  }
};

export const getBlockTestDetails = async (blockTestId) => {
  try {
    const response = await axiosInstance.get(`/test-blocks/${blockTestId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching block test ${blockTestId}:`, error);
    throw error;
  }
};

export const getBlockTestAnswers = async (blockId) => {
  try {
    const response = await axiosInstance.get(`/test-blocks/${blockId}/answers`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching answers for block test ${blockId}:`, error);
    throw error;
  }
};
